import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useRef, useMemo, useState, type ReactNode, type RefObject, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalGuidanceBanner } from '@/modules/portal/components/PortalGuidanceBanner';
import { PortalReadinessBadge } from '@/modules/portal/components/PortalReadinessBadge';
import {
  PortalDaysRemainingDisplay,
  PortalEmptyValue,
  PortalSubscriptionStatusBadge,
} from '@/modules/portal/components/PortalStatusBadges';
import { PortalDetailGrid } from '@/modules/portal/components/ui/PortalDetailGrid';
import { PortalKpiCard } from '@/modules/portal/components/ui/PortalKpiCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalQuickActionCard } from '@/modules/portal/components/ui/PortalQuickActionCard';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalFadeIn, PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import {
  fetchPortalRenewalQuote,
  initiatePortalRenewal,
  uploadPortalInstapayProof,
  type InstapayRecipientDetails,
  type PortalRenewalResult,
} from '@/modules/portal/services/portalRenewal.api';
import {
  buildPortalSupportMailto,
  buildPortalSupportMailtoLabels,
  canPortalSelfServiceRenew,
  derivePortalRenewReadiness,
  formatPortalProductPlan,
} from '@/modules/portal/utils/portalSelfServiceHelpers';
import {
  formatLatestPaymentSummary,
  formatPortalDate,
} from '@/modules/portal/utils/portalOverviewHelpers';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Icons } from '@/shared/components/ui/icons';
import { isApiError } from '@/shared/lib/apiClient';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/utils/money';

type PaymentProviderChoice = 'myfatoorah' | 'instapay';

type RenewalUiState =
  | { kind: 'idle' }
  | {
      kind: 'instapay_pending';
      paymentId: string;
      instapayDetails: InstapayRecipientDetails;
      amountMinor: number;
      currency: string;
    }
  | { kind: 'instapay_proof_uploaded'; paymentId: string }
  | { kind: 'callback'; outcome: string; paymentId?: string };

const PROOF_MAX_SIZE_BYTES = 8 * 1024 * 1024;
const PROOF_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function PortalRenewPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';
  const apiLocale = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const search = useSearch({ strict: false }) as { payment?: string; paymentId?: string };

  const [paymentProvider, setPaymentProvider] = useState<PaymentProviderChoice>('myfatoorah');
  const [uiState, setUiState] = useState<RenewalUiState>(() => {
    if (search.payment) {
      return {
        kind: 'callback',
        outcome: search.payment,
        ...(search.paymentId ? { paymentId: search.paymentId } : {}),
      };
    }
    return { kind: 'idle' };
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofError, setProofError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data,
    isLoading,
    error,
    refetch: refetchOverview,
    isFetching: isOverviewFetching,
  } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
  });

  const readiness = data ? derivePortalRenewReadiness(data) : null;
  const renewalEnabled = readiness !== null && canPortalSelfServiceRenew(readiness);

  const {
    data: renewalQuote,
    isLoading: isQuoteLoading,
    error: quoteError,
    refetch: refetchQuote,
    isFetching: isQuoteFetching,
  } = useQuery({
    queryKey: ['customer', 'subscription', 'renewal-quote'],
    queryFn: fetchPortalRenewalQuote,
    enabled: renewalEnabled,
    retry: false,
  });

  const renewMutation = useMutation({
    mutationFn: (provider: PaymentProviderChoice) =>
      initiatePortalRenewal({
        paymentProvider: provider,
        locale: apiLocale,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (result: PortalRenewalResult) => {
      setSubmitError(null);
      if (result.payment_provider === 'myfatoorah') {
        setUiState({ kind: 'callback', outcome: 'redirecting' });
        window.location.assign(result.payment_url);
        return;
      }
      setUiState({
        kind: 'instapay_pending',
        paymentId: result.payment_id,
        instapayDetails: result.instapay_details,
        amountMinor: result.amount_minor,
        currency: result.currency,
      });
    },
    onError: (err: unknown) => {
      if (isApiError(err) && err.code === 'RENEWAL_PAYMENT_IN_PROGRESS') {
        setSubmitError(t('portal.renew.checkout.pendingPayment'));
        return;
      }
      setSubmitError(isApiError(err) ? err.message : t('portal.renew.checkout.submitError'));
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ paymentId, file }: { paymentId: string; file: File }) =>
      uploadPortalInstapayProof(paymentId, file),
    onSuccess: (_data, variables) => {
      setProofError(null);
      setProofFile(null);
      setUiState({ kind: 'instapay_proof_uploaded', paymentId: variables.paymentId });
    },
    onError: (err: unknown) => {
      setProofError(isApiError(err) ? err.message : t('portal.renew.instapay.upload.error'));
    },
  });

  const billingPeriodLabel = useMemo(() => {
    const period = renewalQuote?.billing_period;
    if (!period) return null;
    return t(`subscriptions.periods.${period}`, { defaultValue: period });
  }, [renewalQuote?.billing_period, t]);

  const formatPaymentStatus = (status: string) =>
    t(`payments.status.${status}`, { defaultValue: status });

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    setProofError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!PROOF_ALLOWED_TYPES.includes(file.type)) {
      setProofError(t('portal.renew.instapay.upload.typeError'));
      e.target.value = '';
      return;
    }
    if (file.size > PROOF_MAX_SIZE_BYTES) {
      setProofError(t('portal.renew.instapay.upload.sizeError'));
      e.target.value = '';
      return;
    }
    setProofFile(file);
  }

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">{t('portal.renew.error.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('portal.renew.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetchOverview()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const hasSubscription = data.subscription_id !== null;
  const paymentSummary = formatLatestPaymentSummary(data, locale, formatPaymentStatus);
  const productPlan = formatPortalProductPlan(data);

  const periodEndLabel =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? t('portal.subscription.fields.trialEnd')
      : t('portal.subscription.fields.periodEnd');

  const periodEndValue =
    data.subscription_status === 'trialing' && data.trial_ends_at
      ? formatPortalDate(data.trial_ends_at, locale)
      : formatPortalDate(data.current_period_end, locale);

  const supportMailto = buildPortalSupportMailto(
    data,
    'renew',
    buildPortalSupportMailtoLabels(data, 'renew', t),
  );

  const contextItems = hasSubscription
    ? [
        {
          id: 'company',
          label: t('portal.renew.fields.company'),
          value: data.tenant_name || <PortalEmptyValue />,
        },
        {
          id: 'product-plan',
          label: t('portal.renew.fields.productPlan'),
          value: productPlan ?? <PortalEmptyValue />,
        },
        {
          id: 'status',
          label: t('portal.renew.fields.subscriptionStatus'),
          value: <PortalSubscriptionStatusBadge status={data.subscription_status} />,
        },
        {
          id: 'period-end',
          label: periodEndLabel,
          value: periodEndValue,
        },
        {
          id: 'remaining',
          label: t('portal.renew.fields.remainingDays'),
          value: <PortalDaysRemainingDisplay overview={data} />,
        },
        {
          id: 'payment',
          label: t('portal.renew.fields.latestPayment'),
          value: paymentSummary ?? <PortalEmptyValue />,
        },
        {
          id: 'readiness',
          label: t('portal.renew.fields.renewalReadiness'),
          value: readiness ? <PortalReadinessBadge namespace="renew" state={readiness} /> : null,
        },
      ]
    : [];

  const guidance =
    readiness === 'payment_attention'
      ? {
          title: t('portal.renew.guidanceAttention.title'),
          message: t('portal.renew.guidanceAttention.message'),
          variant: 'warning' as const,
        }
      : {
          title: t('portal.renew.guidance.title'),
          message: t('portal.renew.guidance.message'),
          variant: 'info' as const,
        };

  const callbackBanner = renderCallbackBanner(uiState, t);

  return (
    <PortalPage
      title={t('portal.pages.renew.title')}
      description={t('portal.renew.description')}
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isOverviewFetching || isQuoteFetching}
          onClick={() => {
            void refetchOverview();
            if (renewalEnabled) void refetchQuote();
          }}
          className="rounded-xl"
        >
          <Icons.RefreshCw
            className={cn(
              'me-2 h-4 w-4',
              (isOverviewFetching || isQuoteFetching) && portalSpinClass,
            )}
          />
          {t('common.refresh')}
        </Button>
      }
    >
      {!hasSubscription ? (
        <PortalFadeIn>
          <div className="portal-empty-state portal-glass-panel max-w-lg">
            <Icons.Layers className="mx-auto h-10 w-10 text-muted-foreground/50" aria-hidden />
            <p className="mt-4 text-lg font-semibold text-foreground">
              {t('portal.renew.empty.title')}
            </p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              {t('portal.renew.empty.message')}
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Link
                to="/portal/subscription"
                className={buttonVariants({ variant: 'outline', className: 'rounded-xl' })}
              >
                {t('portal.renew.actions.backToSubscription')}
              </Link>
              <a
                href={supportMailto}
                className={buttonVariants({ variant: 'default', className: 'rounded-xl' })}
              >
                {t('portal.renew.actions.contactSupport')}
              </a>
            </div>
          </div>
        </PortalFadeIn>
      ) : (
        <>
          {callbackBanner}

          {uiState.kind === 'instapay_proof_uploaded' ? (
            <PortalGuidanceBanner
              title={t('portal.renew.result.instapayProofSubmittedTitle')}
              message={t('portal.renew.result.instapayProofSubmittedMessage')}
              icon={<Icons.CheckCircle className="h-5 w-5" />}
              variant="info"
            />
          ) : uiState.kind === 'instapay_pending' ? (
            <PortalGuidanceBanner
              title={t('portal.renew.result.instapayPendingTitle')}
              message={t('portal.renew.result.instapayPendingMessage')}
              icon={<Icons.Timer className="h-5 w-5" />}
              variant="info"
            />
          ) : (
            <PortalGuidanceBanner
              title={guidance.title}
              message={guidance.message}
              icon={<Icons.RefreshCw className="h-5 w-5" />}
              variant={guidance.variant}
            />
          )}

          <PortalStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <PortalKpiCard label={t('portal.renew.cards.subscriptionStatus')} icon={Icons.Repeat}>
              <PortalSubscriptionStatusBadge status={data.subscription_status} />
            </PortalKpiCard>
            <PortalKpiCard label={t('portal.renew.cards.remainingDays')} icon={Icons.Timer}>
              <PortalDaysRemainingDisplay overview={data} />
            </PortalKpiCard>
          </PortalStagger>

          <PortalDetailGrid
            title={t('portal.renew.sections.context')}
            description={t('portal.renew.sections.contextHint')}
            items={contextItems}
          />

          {/* InstaPay proof-upload panel */}
          {uiState.kind === 'instapay_pending' && (
            <InstapayProofPanel
              paymentId={uiState.paymentId}
              instapayDetails={uiState.instapayDetails}
              amountMinor={uiState.amountMinor}
              currency={uiState.currency}
              locale={locale}
              proofFile={proofFile}
              proofError={proofError}
              isUploading={uploadMutation.isPending}
              onFileChange={handleFileChange}
              onUpload={() => {
                if (proofFile) {
                  uploadMutation.mutate({ paymentId: uiState.paymentId, file: proofFile });
                }
              }}
              onBrowse={() => fileInputRef.current?.click()}
              fileInputRef={fileInputRef}
              t={t}
            />
          )}

          {/* Checkout card — hidden once InstaPay is pending or proof uploaded */}
          {renewalEnabled &&
          uiState.kind !== 'instapay_pending' &&
          uiState.kind !== 'instapay_proof_uploaded' ? (
            <Card className="portal-glass-panel border-border/60">
              <CardHeader>
                <CardTitle className="text-base">{t('portal.renew.checkout.title')}</CardTitle>
                <p className="text-sm text-muted-foreground">{t('portal.renew.checkout.hint')}</p>
              </CardHeader>
              <CardContent className="space-y-5">
                {isQuoteLoading ? (
                  <PortalLoadingState size="sm" message={t('common.loading')} />
                ) : quoteError ? (
                  <div className="space-y-3">
                    <p className="text-sm text-destructive">
                      {t('portal.renew.checkout.quoteError')}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void refetchQuote()}
                    >
                      {t('common.retry')}
                    </Button>
                  </div>
                ) : renewalQuote ? (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('portal.renew.checkout.total')}
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-foreground">
                          {formatMoney(
                            renewalQuote.total_minor,
                            renewalQuote.currency,
                            locale === 'ar-SA' ? 'ar' : 'en',
                          )}
                        </p>
                      </div>
                      <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('portal.renew.checkout.billingPeriod')}
                        </p>
                        <p className="mt-1 text-lg font-semibold text-foreground">
                          {billingPeriodLabel ?? <PortalEmptyValue />}
                        </p>
                      </div>
                    </div>

                    <fieldset className="space-y-3">
                      <legend className="text-sm font-medium text-foreground">
                        {t('portal.renew.checkout.paymentMethod')}
                      </legend>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {(['myfatoorah', 'instapay'] as const).map((provider) => (
                          <label
                            key={provider}
                            className={cn(
                              'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors',
                              paymentProvider === provider
                                ? 'border-primary bg-primary/5'
                                : 'border-border/60 hover:border-border',
                            )}
                          >
                            <input
                              type="radio"
                              name="renewal-payment-provider"
                              value={provider}
                              checked={paymentProvider === provider}
                              onChange={() => setPaymentProvider(provider)}
                              className="mt-1"
                            />
                            <span>
                              <span className="block text-sm font-medium text-foreground">
                                {t(`portal.renew.checkout.methods.${provider}`)}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {submitError ? (
                      <p className="text-sm text-destructive" role="alert">
                        {submitError}
                      </p>
                    ) : null}

                    <Button
                      type="button"
                      className="rounded-xl"
                      disabled={renewMutation.isPending}
                      onClick={() => {
                        setSubmitError(null);
                        renewMutation.mutate(paymentProvider);
                      }}
                    >
                      {renewMutation.isPending ? (
                        <>
                          <Icons.RefreshCw className={cn('me-2 h-4 w-4', portalSpinClass)} />
                          {t('portal.renew.checkout.renewing')}
                        </>
                      ) : (
                        t('portal.renew.checkout.renewCta')
                      )}
                    </Button>
                  </>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <section className="space-y-4">
            <h2 className="text-base font-semibold text-foreground">
              {t('portal.renew.actions.title')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('portal.renew.actions.hint')}</p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <a
                href={supportMailto}
                className={buttonVariants({ variant: 'outline', className: 'rounded-xl' })}
              >
                {t('portal.renew.actions.contactSupport')}
              </a>
              <Link
                to="/portal/subscription"
                className={buttonVariants({ variant: 'outline', className: 'rounded-xl' })}
              >
                {t('portal.renew.actions.backToSubscription')}
              </Link>
              {(uiState.kind === 'instapay_pending' ||
                uiState.kind === 'instapay_proof_uploaded' ||
                uiState.kind === 'callback') && (
                <Link
                  to="/portal/payments"
                  className={buttonVariants({ variant: 'default', className: 'rounded-xl' })}
                >
                  {t('portal.renew.result.viewPayments')}
                </Link>
              )}
            </div>
            <PortalStagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <PortalQuickActionCard
                to="/portal/payments"
                label={t('portal.renew.actions.viewPayments')}
                description={t('portal.renew.actions.viewPaymentsHint')}
                icon={Icons.DollarSign}
              />
              <PortalQuickActionCard
                to="/portal/subscription"
                label={t('portal.renew.actions.backToSubscription')}
                description={t('portal.renew.actions.backToSubscriptionHint')}
                icon={Icons.Repeat}
              />
            </PortalStagger>
          </section>

          <p className="text-center text-xs text-muted-foreground">{t('portal.renew.safeNote')}</p>
        </>
      )}
    </PortalPage>
  );
}

function InstapayProofPanel({
  paymentId,
  instapayDetails,
  amountMinor,
  currency,
  locale,
  proofFile,
  proofError,
  isUploading,
  onFileChange,
  onUpload,
  onBrowse,
  fileInputRef,
  t,
}: {
  paymentId: string;
  instapayDetails: InstapayRecipientDetails;
  amountMinor: number;
  currency: string;
  locale: string;
  proofFile: File | null;
  proofError: string | null;
  isUploading: boolean;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  onBrowse: () => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  t: (key: string) => string;
}) {
  const amountLabel = formatMoney(amountMinor, currency, locale === 'ar-SA' ? 'ar' : 'en');

  const copyValue = (value: string) => {
    void navigator.clipboard?.writeText(value);
  };

  const frontendFallbackInstapayName = 'Wared Technology';
  const frontendFallbackInstapayPhone = '01031846418';

  const transferRows = [
    {
      label: 'اسم المستفيد',
      value: instapayDetails.recipient_name || frontendFallbackInstapayName,
    },
    {
      label: 'رقم التحويل / InstaPay',
      value: instapayDetails.recipient_phone || frontendFallbackInstapayPhone,
      dir: 'ltr' as const,
    },
    {
      label: 'IBAN',
      value: instapayDetails.recipient_iban,
      dir: 'ltr' as const,
    },
  ].filter((row) => Boolean(row.value));

  return (
    <Card className="portal-glass-panel overflow-hidden border-primary/20">
      <CardHeader className="border-b border-border/50 bg-primary/5 text-center">
        <CardTitle className="text-xl font-bold text-foreground">الدفع عبر InstaPay</CardTitle>
        <p className="mx-auto max-w-xl text-sm leading-6 text-muted-foreground">
          حوّل المبلغ المطلوب إلى بيانات الاستلام التالية، ثم ارفع صورة إيصال التحويل للمراجعة.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 p-6">
        <div className="rounded-3xl border border-primary/20 bg-primary/5 p-5 text-center">
          <p className="text-sm font-medium text-muted-foreground">المبلغ المطلوب</p>
          <p className="mt-2 text-4xl font-extrabold tracking-tight text-foreground">
            {amountLabel}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            يرجى تحويل نفس المبلغ الظاهر بدون تقريب.
          </p>
        </div>

        <div className="grid gap-3">
          {transferRows.map((row) => (
            <div
              key={row.label}
              className="rounded-2xl border border-border/60 bg-background/70 p-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 text-right">
                  <p className="text-xs font-medium text-muted-foreground">{row.label}</p>
                  <p
                    className="mt-1 break-all text-base font-bold text-foreground"
                    dir={row.dir ?? 'rtl'}
                  >
                    {row.value}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => copyValue(String(row.value))}
                >
                  نسخ
                </Button>
              </div>
            </div>
          ))}

          {transferRows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/70 bg-muted/20 p-5 text-center">
              <p className="text-sm font-medium text-foreground">بيانات التحويل غير متاحة حاليًا</p>
              <p className="mt-1 text-sm text-muted-foreground">
                تواصل مع الدعم لإرسال بيانات InstaPay.
              </p>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-border/60 bg-muted/20 p-4">
          <p className="text-sm font-semibold text-foreground">خطوات التجديد</p>
          <ol className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">
            <li>1. افتح تطبيق InstaPay أو تطبيق البنك.</li>
            <li>2. حوّل المبلغ المطلوب إلى رقم التحويل الموضح.</li>
            <li>3. احتفظ بصورة إيصال الدفع.</li>
            <li>4. ارفع الإيصال من الزر التالي ليتم مراجعة الطلب.</li>
          </ol>
        </div>

        <div className="rounded-3xl border border-dashed border-border/70 bg-background/80 p-5 text-center">
          <p className="text-base font-bold text-foreground">رفع إثبات الدفع</p>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">
            ارفع صورة واضحة من إيصال التحويل. الصيغ المدعومة: JPG, PNG, WEBP, GIF.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            aria-label={t('portal.renew.instapay.upload.button')}
            onChange={onFileChange}
          />

          <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-xl px-5"
              onClick={onBrowse}
              disabled={isUploading}
            >
              <Icons.Upload className="me-2 h-4 w-4" />
              اختيار صورة الإيصال
            </Button>

            {proofFile && (
              <span className="max-w-[260px] truncate rounded-xl bg-muted px-3 py-2 text-sm text-muted-foreground">
                {proofFile.name}
              </span>
            )}
          </div>

          {proofError && (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {proofError}
            </p>
          )}

          {proofFile && (
            <Button
              type="button"
              className="mt-4 h-11 rounded-xl px-6 font-bold"
              disabled={isUploading}
              onClick={onUpload}
            >
              {isUploading ? (
                <>
                  <Icons.RefreshCw className={cn('me-2 h-4 w-4', portalSpinClass)} />
                  جاري رفع الإيصال…
                </>
              ) : (
                <>
                  <Icons.CheckCircle className="me-2 h-4 w-4" />
                  إرسال الإيصال للمراجعة
                </>
              )}
            </Button>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground" data-payment-id={paymentId}>
          احتفظ برقم مرجع التحويل حتى يتم تأكيد التجديد.
        </p>
      </CardContent>
    </Card>
  );
}

function renderCallbackBanner(uiState: RenewalUiState, t: (key: string) => string): ReactNode {
  if (uiState.kind !== 'callback') return null;

  if (uiState.outcome === 'redirecting') {
    return (
      <PortalGuidanceBanner
        title={t('portal.renew.result.myfatoorahRedirect')}
        message={t('portal.renew.checkout.renewing')}
        icon={<Icons.RefreshCw className="h-5 w-5" />}
        variant="info"
      />
    );
  }

  if (uiState.outcome === 'succeeded') {
    return (
      <PortalGuidanceBanner
        title={t('portal.renew.result.paymentSucceededTitle')}
        message={t('portal.renew.result.paymentSucceededMessage')}
        icon={<Icons.CheckCircle className="h-5 w-5" />}
        variant="info"
      />
    );
  }

  if (uiState.outcome === 'pending') {
    return (
      <PortalGuidanceBanner
        title={t('portal.renew.result.paymentPendingTitle')}
        message={t('portal.renew.result.paymentPendingMessage')}
        icon={<Icons.Timer className="h-5 w-5" />}
        variant="warning"
      />
    );
  }

  if (
    uiState.outcome === 'failed' ||
    uiState.outcome === 'cancelled' ||
    uiState.outcome === 'expired'
  ) {
    return (
      <PortalGuidanceBanner
        title={t('portal.renew.result.paymentFailedTitle')}
        message={t('portal.renew.result.paymentFailedMessage')}
        icon={<Icons.XCircle className="h-5 w-5" />}
        variant="warning"
      />
    );
  }

  return null;
}
